import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { ColumnDef, SortingState } from "@/components/ui/data-table";
import { DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilters } from "@/components/ui/mobile-filters";
import {
	CENNIKI_CENTRALNE_PAGE_SIZE,
	useCennikiCentralne,
} from "@/features/strateg-cenowy/hooks/use-cenniki-centralne";
import { fetchCentralPricing } from "@/features/strateg-cenowy/strateg-api";
import type { CentralPricingItem, CentralPricingSort } from "@/features/strateg-cenowy/types";
import { useStrategScope } from "@/features/strateg-cenowy/use-strateg-scope";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { ensureElementVisible } from "@/lib/route-guards";
import { encodeScope } from "@/lib/scope";
import { useDebounce } from "@/lib/use-debounce";

const SORT_FROM_COL: Record<string, CentralPricingSort> = {
	pharmacyName: "pharmacy",
	bloz: "bloz",
	productName: "product",
	cenaNetto: "cena",
	cennikName: "cennik",
	validFrom: "valid_from",
	validTo: "valid_to",
};

const COL_FROM_SORT: Record<CentralPricingSort, string> = {
	pharmacy: "pharmacyName",
	bloz: "bloz",
	product: "productName",
	cena: "cenaNetto",
	cennik: "cennikName",
	valid_from: "validFrom",
	valid_to: "validTo",
};

export const Route = createFileRoute("/_auth/strateg-cenowy/cenniki-centralne")({
	component: CennikiCentralneRoute,
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.strateg-cenowy.cenniki-centralne",
			location.pathname,
		);
	},
});

function CennikiCentralneRoute() {
	const { scope } = useStrategScope();
	const isBelowMd = useIsBelowMd();
	const [includeArchived, setIncludeArchived] = useState(false);
	const [searchRaw, setSearchRaw] = useState("");
	const search = useDebounce(searchRaw.trim(), 300);
	const [sort, setSort] = useState<CentralPricingSort | null>("cennik");
	const [dir, setDir] = useState<"asc" | "desc">("asc");

	const query = useCennikiCentralne({ scope, includeArchived, search, sort, dir });

	const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data?.pages]);
	const total = query.data?.pages[query.data.pages.length - 1]?.total;

	const handleFetchMore = useCallback(() => {
		if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
	}, [query]);

	const sortingState: SortingState = useMemo(
		() => (sort ? [{ id: COL_FROM_SORT[sort], desc: dir === "desc" }] : []),
		[sort, dir],
	);

	const handleSortingChange = useCallback(
		(updater: SortingState | ((s: SortingState) => SortingState)) => {
			const next = typeof updater === "function" ? updater(sortingState) : updater;
			const first = next[0];
			if (!first) {
				setSort(null);
				return;
			}
			const mapped = SORT_FROM_COL[first.id];
			if (!mapped) return;
			setSort(mapped);
			setDir(first.desc ? "desc" : "asc");
		},
		[sortingState],
	);

	const fetchAllForExport = useCallback(async (): Promise<Record<string, unknown>[]> => {
		const HARD_CAP = 100_000;
		const allItems: CentralPricingItem[] = [];
		const scopeKey = encodeScope(scope);
		let offset = 0;
		while (offset < HARD_CAP) {
			const page = await fetchCentralPricing({
				scope: scopeKey,
				includeArchived,
				search: search || undefined,
				sort: sort ?? undefined,
				dir,
				limit: CENNIKI_CENTRALNE_PAGE_SIZE,
				offset,
			});
			allItems.push(...page.items);
			if (page.items.length < CENNIKI_CENTRALNE_PAGE_SIZE) break;
			if (allItems.length >= page.total) break;
			offset += CENNIKI_CENTRALNE_PAGE_SIZE;
		}
		if (allItems.length >= HARD_CAP) {
			// eslint-disable-next-line no-alert
			// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
			alert(
				`Eksport przerwany na ${HARD_CAP.toLocaleString("pl-PL")} wierszach (limit). ` +
					`Zaweż scope albo użyj wyszukiwarki aby zmniejszyć zestaw.`,
			);
		}
		return allItems.map((it) => ({
			pharmacyName: it.pharmacyName,
			bloz: it.bloz,
			productName: it.productName ?? "",
			dosage: it.dosage ?? "",
			pack: it.pack ?? "",
			cenaNetto: it.cenaNetto,
			krotnosc: it.krotnosc ?? "",
			cennikName: it.cennikName,
			punktDostepu: it.punktDostepu,
			validFrom: it.validFrom,
			validTo: it.validTo,
			cennikTyp: it.cennikTyp ?? "",
			ean: it.ean ?? "",
		}));
	}, [scope, includeArchived, search, sort, dir]);

	const columns = useMemo<ColumnDef<CentralPricingItem>[]>(
		() => [
			{
				id: "pharmacyName",
				accessorKey: "pharmacyName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Apteka" />,
				enableSorting: true,
				// Lista na telefonie: produkt = tytul, apteka + cennik = podtytul,
				// cena netto = miara (bez `isNumeric`, ktore zmienia wyrownanie na desktopie).
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "bloz",
				accessorKey: "bloz",
				header: ({ column }) => <DataTableColumnHeader column={column} title="BLOZ" />,
				enableSorting: true,
				cell: ({ row }) => <span className="font-mono text-xs">{row.original.bloz}</span>,
			},
			{
				id: "productName",
				accessorKey: "productName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Nazwa produktu" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.productName ?? <span className="text-muted-foreground">—</span>,
				meta: { mobileRole: "title" },
			},
			{
				id: "dosage",
				accessorKey: "dosage",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dawka" />,
				enableSorting: false,
				cell: ({ row }) => row.original.dosage ?? <span className="text-muted-foreground">—</span>,
			},
			{
				id: "pack",
				accessorKey: "pack",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Opakowanie" />,
				enableSorting: false,
				cell: ({ row }) => row.original.pack ?? <span className="text-muted-foreground">—</span>,
			},
			{
				id: "cenaNetto",
				accessorKey: "cenaNetto",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Cena netto" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.cenaNetto != null ? (
						<span className="tabular-nums">{row.original.cenaNetto.toFixed(2)}</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
				meta: { mobileRole: "measure", mobileLabel: "Cena netto" },
			},
			{
				id: "krotnosc",
				accessorKey: "krotnosc",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Krotnosc" />,
				enableSorting: false,
				cell: ({ row }) =>
					row.original.krotnosc != null ? (
						<span className="tabular-nums">{row.original.krotnosc.toFixed(2)}</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				id: "cennikName",
				accessorKey: "cennikName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Cennik" />,
				enableSorting: true,
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "punktDostepu",
				accessorKey: "punktDostepu",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Punkt dostepu" />,
				enableSorting: false,
			},
			{
				id: "validFrom",
				accessorKey: "validFrom",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Wazny od" />,
				enableSorting: true,
				cell: ({ row }) => <span className="tabular-nums">{row.original.validFrom}</span>,
			},
			{
				id: "validTo",
				accessorKey: "validTo",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Wazny do" />,
				enableSorting: true,
				cell: ({ row }) => <span className="tabular-nums">{row.original.validTo}</span>,
			},
			{
				id: "cennikTyp",
				accessorKey: "cennikTyp",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Typ" />,
				enableSorting: false,
				cell: ({ row }) =>
					row.original.cennikTyp ?? <span className="text-muted-foreground">—</span>,
			},
			{
				id: "ean",
				accessorKey: "ean",
				header: ({ column }) => <DataTableColumnHeader column={column} title="EAN" />,
				enableSorting: false,
				cell: ({ row }) => <span className="font-mono text-xs">{row.original.ean ?? ""}</span>,
			},
		],
		[],
	);

	const searchField = (
		<div className="flex flex-col gap-1 w-full max-w-sm">
			<Label htmlFor="cenniki-c-search" className="text-xs text-muted-foreground">
				Szukaj (nazwa produktu lub BLOZ)
			</Label>
			<Input
				id="cenniki-c-search"
				value={searchRaw}
				onChange={(e) => setSearchRaw(e.target.value)}
				placeholder="np. APAP albo 0007891"
			/>
		</div>
	);
	const archivedToggle = (
		<label className="flex items-center gap-2 text-sm pb-2 cursor-pointer max-md:min-h-9 max-md:pb-0">
			<input
				type="checkbox"
				className="size-4 cursor-pointer accent-primary"
				checked={includeArchived}
				onChange={(e) => setIncludeArchived(e.target.checked)}
			/>
			<span>Pokaz archiwalne/przyszle cenniki</span>
		</label>
	);
	const rowsLabel = total != null ? `${items.length} z ${total} wierszy` : "Wczytywanie...";

	return (
		<div className="flex flex-col h-full p-3 md:p-4 gap-3 md:gap-4">
			{/* Telefon: szukajka zostaje nad lista, przelacznik archiwum w chipie i arkuszu. */}
			{isBelowMd && searchField}
			<MobileFilters
				className="rounded-lg border"
				chips={[
					{
						id: "archived",
						label: "Cenniki",
						value: includeArchived ? "Z archiwalnymi i przyszłymi" : "Tylko aktualne",
						active: includeArchived,
						picker: archivedToggle,
					},
				]}
				sheetContent={archivedToggle}
				onClear={() => setIncludeArchived(false)}
				resultLabel={total != null ? `Pokaż ${total} wierszy` : "Pokaż wyniki"}
			>
				<div className="flex flex-wrap items-end gap-3 md:gap-4">
					{searchField}
					{archivedToggle}
					<div className="text-xs text-muted-foreground ml-auto pb-2">{rowsLabel}</div>
				</div>
			</MobileFilters>

			<DataTable<CentralPricingItem>
				tableId="strateg-cenowy-cenniki-centralne"
				columns={columns}
				data={items}
				isLoading={(query.isFetching || query.isPending) && !query.isFetchingNextPage}
				loadingLabel="Wczytywanie cennikow centralnych..."
				totalCount={total}
				sorting={sortingState}
				onSortingChange={handleSortingChange}
				manualSorting
				onFetchMore={handleFetchMore}
				hasNextPage={query.hasNextPage}
				isFetchingNextPage={query.isFetchingNextPage}
				enableToolbar
				enableColumnVisibility
				enableColumnPinning
				enableSchemes
				enableColumnColoring
				defaultPinnedColumns={["pharmacyName"]}
				columnLabels={{
					pharmacyName: "Apteka",
					bloz: "BLOZ",
					productName: "Nazwa produktu",
					dosage: "Dawka",
					pack: "Opakowanie",
					cenaNetto: "Cena netto",
					krotnosc: "Krotnosc",
					cennikName: "Cennik",
					punktDostepu: "Punkt dostepu",
					validFrom: "Wazny od",
					validTo: "Wazny do",
					cennikTyp: "Typ",
					ean: "EAN",
				}}
				showSummaryRow
				summableColumns={["cenaNetto"]}
				summaryLabel="Srednia / Suma"
				deriveSummary={({ sums, rowCount }) => ({
					pharmacyName: `${rowCount} pozycji`,
					cenaNetto:
						rowCount > 0
							? `sr. ${(sums.cenaNetto / rowCount).toFixed(2)} (suma ${sums.cenaNetto.toFixed(2)})`
							: "—",
				})}
				enableExport
				exportFileName="cenniki-centralne"
				fetchAllForExport={fetchAllForExport}
				className="flex-1 min-h-0"
			/>
		</div>
	);
}
